import Inherit from '../../../core/inherit';

import ShelfController from '../../../core/shelf.controller';
import { TraditionalStores, productList } from '../../common';
import { User } from '../../../data';
import { can } from '../../../core/access';
import SegmentDelegate from './segment.delegate';
import { isFeatureEnabled } from '../../../company/session';
import { FEATURES } from '../../../company/features';
import { applySelection, buildStoreSegmentations } from './storeSegmentation';

const MODERN_STORE_TYPES = ['nacional', 'regional', 'independiente'];

class SegmentController extends Inherit(ShelfController, SegmentDelegate) {
    async initialize() {
        let clients = null;
        let storeType = null;
        let products = [];
        let defaultProducts = [];

        const id = this.props.searchParams.get('id');

        if (this.isAuthenticated) {
            try {
                clients = await this.data.fetchClients();
                if (id) {
                    storeType = await this.data.fetchStoreTypeByValue(id);
                    products = storeType?.products ?? [];
                    defaultProducts = [...products];
                }
            } catch (err) {
                this.props.onMessage?.('error', err?.response?.data?.message || err?.message || 'Error al cargar segmentación');
            }
        } else this.navigateLogin();

        this.setState({
            storeType,
            clients,
            products,
            defaultProducts,
            loading: false,
            viewMode: 'default',
            selectedStoreIds: [],
            clientStores: [],
            storeSegmentations: new Map(),
            hasOverride: false,
            canManageOverrides: isFeatureEnabled(FEATURES.STORE_OVERRIDES) && can(User.getInstance()?.userType, 'store-overrides.manage'),
        });
    }

    /** Create flow: picking the segment type loads it and remembers its display name. */
    selectSegmentType(value) {
        const { channel, clients } = this.state;
        this.fetchStoreType(value);
        const segment =
            channel === 'moderno'
                ? clients?.filter((client) => client.channel === 'moderno')?.find((c) => c._id === value)?.name
                : TraditionalStores.find((c) => c.value === value)?.label;
        this.setSegment(segment);
    }

    /** Stores currently selected in "Excepciones por piso" mode (all with the same segmentation). */
    getSelectedStores() {
        const { clientStores, selectedStoreIds } = this.state;
        const selected = new Set(selectedStoreIds);
        return clientStores.filter((s) => selected.has(String(s._id)));
    }

    /** Products added/removed in the selected pisos vs. the client's default segmentation. */
    getOverrideDiff() {
        const { viewMode, selectedStoreIds, products, defaultProducts } = this.state;
        if (viewMode !== 'exception' || !selectedStoreIds.length) return null;
        const clientSet = new Set(defaultProducts);
        const storeSet = new Set(products);
        return {
            added: products.filter((p) => !clientSet.has(p)).length,
            removed: defaultProducts.filter((p) => !storeSet.has(p)).length,
        };
    }

    toggleProduct(productValue) {
        const { products } = this.state;
        if (products?.includes(productValue)) {
            this.setProducts(products.filter((p) => p !== productValue));
        } else {
            this.setProducts([...products, productValue]);
        }
    }

    toggleCategory(categoryName) {
        const { products } = this.state;
        const categoryProducts = productList?.filter((p) => p?.category === categoryName).map((p) => p.value);
        const allSelected = categoryProducts?.every((pv) => products?.includes(pv));

        if (allSelected) {
            // Deselect all products in this category
            this.setProducts(products.filter((p) => !categoryProducts.includes(p)));
        } else {
            // Select all products in this category
            this.setProducts([...new Set([...products, ...categoryProducts])]);
        }
    }

    async fetchStoreType(value) {
        this.setState({ loading: true });
        try {
            const storeType = await this.data.fetchStoreTypeByValue(value);
            const products = storeType?.products ?? [];
            this.setState({
                storeType,
                products,
                defaultProducts: [...products],
                loading: false,
                viewMode: 'default',
                selectedStoreIds: [],
                clientStores: [],
                storeSegmentations: new Map(),
                hasOverride: false,
            });
        } catch (err) {
            this.props.onMessage?.('error', err?.response?.data?.message || err?.message || 'Error al cargar segmentación');
            this.setState({ loading: false });
        }
    }

    async onViewModeChange(viewMode) {
        this.setViewMode(viewMode);
        if (viewMode === 'exception') {
            await this.loadClientStores();
            this.setState({ products: [...this.state.defaultProducts] });
        } else {
            this.setState({ products: [...this.state.defaultProducts], hasOverride: false });
        }
    }

    async loadClientStores() {
        const { storeType, clients, segment, channel } = this.state;
        let clientId = storeType?.value;

        if (!clientId && channel === 'moderno' && segment) {
            clientId = clients.find((c) => c.name === segment)?._id;
        }

        if (!clientId) {
            this.props.onMessage?.('warning', 'No se encontró el cliente para cargar pisos de venta');
            return;
        }

        try {
            const [stores, overrides] = await Promise.all([
                this.data.fetchStoresByClient(clientId),
                this.data.fetchStoreTypeOverrides(clientId).catch(() => []),
            ]);
            // Per-piso segmentation only applies to MODERNO stores.
            const clientStores = (stores || []).filter((s) => MODERN_STORE_TYPES.includes(s.storeType));
            const storeSegmentations = buildStoreSegmentations(clientStores, overrides, this.state.defaultProducts);
            const overrideStoreIds = new Set([...storeSegmentations].filter(([, info]) => info.hasOverride).map(([id]) => id));
            this.setState({ clientStores, storeSegmentations, overrideStoreIds });
        } catch (err) {
            this.props.onMessage?.('error', err?.response?.data?.message || err?.message || 'Error al cargar pisos de venta');
        }
    }

    /**
     * The piso picker changed. Pisos can be edited together only when they use the same products
     * (./storeSegmentation.js); a store with a different segmentation is not added.
     */
    onStoresSelect(nextIds) {
        const { storeSegmentations, defaultProducts } = this.state;
        const { ids, refused } = applySelection(nextIds, storeSegmentations);
        if (refused) {
            this.props.onMessage?.('warning', 'Solo puedes seleccionar juntos pisos que usan los mismos productos');
        }
        const shared = ids.length ? storeSegmentations.get(ids[0]) : null;
        this.setState({
            selectedStoreIds: ids,
            products: shared ? [...shared.products] : [...defaultProducts],
            hasOverride: ids.some((id) => storeSegmentations.get(id)?.hasOverride),
        });
    }

    /** Adds every piso that uses the same products as the current selection. */
    selectAllMatching() {
        const { clientStores, selectedStoreIds, storeSegmentations } = this.state;
        if (!selectedStoreIds.length) return;
        const key = storeSegmentations.get(selectedStoreIds[0])?.key;
        const matching = clientStores.map((s) => String(s._id)).filter((id) => storeSegmentations.get(id)?.key === key);
        this.onStoresSelect([...selectedStoreIds, ...matching.filter((id) => !selectedStoreIds.includes(id))]);
    }

    /** Marks `storeIds` as using `products` (own exception) or the client default (products null). */
    applySegmentationLocally(storeIds, products) {
        const { storeSegmentations, defaultProducts } = this.state;
        const next = new Map(storeSegmentations);
        const updated = buildStoreSegmentations(
            storeIds.map((_id) => ({ _id })),
            products ? storeIds.map((storeId) => ({ storeId, products })) : [],
            defaultProducts
        );
        for (const [id, info] of updated) next.set(id, info);
        const overrideStoreIds = new Set([...next].filter(([, info]) => info.hasOverride).map(([id]) => id));
        return { storeSegmentations: next, overrideStoreIds };
    }

    async deleteOverride() {
        const { selectedStoreIds, defaultProducts } = this.state;
        if (!selectedStoreIds.length) return;
        this.setState({ deleting: true });
        try {
            const res = await this.data.removeStoreTypeOverrides(selectedStoreIds);
            if (!res?.success) throw new Error(res?.message || 'No se pudo eliminar la excepción');
            this.setState({
                ...this.applySegmentationLocally(selectedStoreIds, null),
                products: [...defaultProducts],
                hasOverride: false,
                deleting: false,
            });
            const many = selectedStoreIds.length > 1;
            this.props.onMessage?.(
                'success',
                many ? `Excepción eliminada en ${selectedStoreIds.length} pisos: vuelven a la segmentación del cliente` : 'Excepción eliminada: el piso vuelve a la segmentación del cliente'
            );
        } catch (err) {
            this.props.onMessage?.('error', err?.response?.data?.message || err?.message || 'No se pudo eliminar la excepción');
            this.setState({ deleting: false });
        }
    }

    async createStoreType() {
        const { storeType, segment, products, channel, clients, viewMode, selectedStoreIds } = this.state;
        const { onMessage } = this.props;

        this.setState({ disabled: true });

        try {
            if (viewMode === 'exception') {
                if (!selectedStoreIds.length) {
                    onMessage?.('warning', 'Selecciona al menos un piso de venta');
                    this.setState({ disabled: false });
                    return;
                }

                if (!products?.length) {
                    onMessage?.('warning', 'La segmentación del piso debe tener al menos un producto');
                    this.setState({ disabled: false });
                    return;
                }

                // One request for every selected piso; the API re-checks they share a segmentation.
                const response = await this.data.saveStoreTypeOverrides(selectedStoreIds, products);
                const saved = Boolean(response?.success);
                this.setState({ disabled: false, hasOverride: saved });
                if (saved) {
                    const many = selectedStoreIds.length > 1;
                    onMessage?.('success', many ? `Excepción guardada en ${selectedStoreIds.length} pisos` : 'Excepción guardada correctamente');
                    this.navigateSegments();
                } else {
                    onMessage?.('error', response?.message || 'Error al guardar excepción');
                }
                return;
            }

            let value = storeType?.value;

            if (!storeType?.label) {
                value =
                    channel === 'moderno'
                        ? clients.find((c) => c.name === segment)?._id
                        : TraditionalStores.find((c) => c.label === segment)?.value;
            }

            const payload = {
                label: storeType?.label ? storeType.label : segment,
                channel: storeType?.label ? storeType?.channel : channel,
                products: products,
                value: value,
            };

            const response = await this.data.createStoreType(payload);
            this.setState({ disabled: false });
            if (response) {
                onMessage?.('success', 'Segmentación guardada correctamente');
                this.navigateSegments();
            } else {
                onMessage?.('error', 'Error al guardar segmentación');
            }
        } catch (err) {
            onMessage?.('error', err?.response?.data?.message || err?.message || 'Error al guardar');
            this.setState({ disabled: false });
        }
    }
}

export default SegmentController;
